import { forwardRef, useContext, useImperativeHandle, useRef } from "react"
import { FocusedView, ItemsContext, OnTemplateAdd } from "../../../Items/ItemsRoot"
import { CatalogItem } from "../../../../domain/catalogItem"
import { CatalogTemplateWithItems } from "../../../../domain/catalogTemplate"
import { findWorkareaIdForWorkareaItem, getNewSortIndex, getServerState } from "../../../Items/catalogBrowserContextModel"
import { ActualItemOrigins, WorkareaItemTypes } from "../../../../common/constants"
import CatalogBrowserContext, { ItemHandlerRef } from "../../../ItemCatalog/CatalogBrowserContext"
import { viewItemForDraggableCatalogItem, viewItemForDraggableTemplate } from "../../../Items/itemModel"

type Props = {
  onTemplateAdd: OnTemplateAdd
  workareaId: number
  focusedView: FocusedView
}

const ItemHandler = forwardRef(({ onTemplateAdd, workareaId, focusedView }: Props, ref) => {
  const { dispatch, jobItemsQuery, changeSet, jobData } = useContext(ItemsContext)
  const containerRef = useRef<any>()

  const items = {
    estimated: jobItemsQuery.data?.estimatedItems ?? [],
    actual: jobItemsQuery.data?.actualItems ?? [],
    added: changeSet.addedItems,
    catalogItems: jobItemsQuery.data?.catalogItems ?? [],
    templateItems: jobItemsQuery.data?.templateItems ?? []
  }

  const addCatalogItem = (catalogItem: CatalogItem, toGuid?: string) => {
    const serverState = getServerState(workareaId, focusedView, items)
    const sortIndex = getNewSortIndex(workareaId, serverState, changeSet.addedItems)

    dispatch({
      type: 'ADD_ITEM_FROM_CATALOG',
      catalogItem,
      addItemParams: {
        quantity: 0,
        workareaId,
        workareas: jobData.workareas,
        toGuid,
        budgets: jobData.budgets,
        workareaItemType: focusedView === 'workarea-estimated' ? WorkareaItemTypes.Estimated : WorkareaItemTypes.Actual,
        sortIndex,
        origin: ActualItemOrigins.Web,
        serverState,
      }
    })
  }

  const addTemplate = (template: CatalogTemplateWithItems, toGuid?: string) => {
    const serverState = getServerState(workareaId, focusedView, items)
    const sortIndex = getNewSortIndex(workareaId, serverState, changeSet.addedItems)

    dispatch({
      type: 'ADD_TEMPLATE_FROM_CATALOG',
      template,
      addItemParams: {
        quantity: 0,
        workareaId: workareaId,
        workareas: jobData.workareas,
        toGuid,
        budgets: jobData.budgets,
        workareaItemType: focusedView === 'workarea-estimated' ? WorkareaItemTypes.Estimated : WorkareaItemTypes.Actual,
        sortIndex,
        origin: focusedView === 'receipt' ? ActualItemOrigins.Receipt : ActualItemOrigins.Web,
        serverState,
      }
    })

    onTemplateAdd(workareaId, template)
  }

  useImperativeHandle(ref, () => ({
    addCatalogItem: addCatalogItem,
    addTemplate: addTemplate,
    updateSorting: (fromGuid, toGuid) => {
      const workareaId = findWorkareaIdForWorkareaItem(fromGuid, items)
      const serverState = getServerState(workareaId, focusedView, items)
      dispatch({ type: 'UPDATE_SORTING', fromGuid, toGuid, serverState })
    },
    findOverlayItem: (guid) => {
      return changeSet.updatedItems.find(x => x.guid === guid)
        ?? changeSet.addedItems.find(x => x.guid === guid)
        ?? items.estimated.find(x => x.guid === guid)
        ?? items.actual.find(x => x.guid === guid)
    },
    getViewItemForDraggableCatalogItem: (catalogItem, dragId) => {
      return viewItemForDraggableCatalogItem(catalogItem, dragId)
    },
    getViewItemForDraggableTemplate: (template, dragId) => {
      return viewItemForDraggableTemplate(template, dragId)
    }
  } as ItemHandlerRef))

  return <div ref={containerRef}>
  </div>
})

const CatalogItemHandler = ({ onTemplateAdd, focusedView, workareaId }: Props) => {
  const { itemHandlerRef } = useContext(CatalogBrowserContext)
  return <ItemHandler
    ref={itemHandlerRef}
    onTemplateAdd={onTemplateAdd}
    focusedView={focusedView}
    workareaId={workareaId}
  />
}

export default CatalogItemHandler
